import { FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { TimePicker as MuiTimePicker, TimePickerProps } from '@mui/x-date-pickers-pro';
import FormLabel from './FormLabel';
import { AppInputCustomProps, CustomFieldProps } from './types';
import { Dayjs } from 'dayjs';
import classNames from 'classnames/bind';
import useDebouncer from '@hooks/useDebounce';
import { CustomTimePickerAdornment } from './DateRangePicker';
import ClearInputAdornment from './ClearInputAdornment';

import styles from './Form.module.css';
const styleContext = classNames.bind(styles);

type AppTimePickerProps = TimePickerProps & AppInputCustomProps;

/**
 * Wrapper around MUI TimePicker to provide application-wide default behaviors.
 */
export default function TimePicker({ customProps, ...props }: AppTimePickerProps) {
  const dynamicClasses = styleContext({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: props.disabled,
    empty: !props.value
  });

  return (
    <MuiTimePicker
      {...props}
      className={`${props.className ?? ''} ${dynamicClasses}`}
      label={
        <FormLabel
          text={customProps.labelText}
          error={customProps.isError}
          errorText={customProps.errorText}
          required={customProps.required}
        />
      }
      disabled={props.disabled}
      slotProps={{
        textField: {
          onBlur: customProps.onBlur as FocusEventHandler<HTMLDivElement>,
          error: customProps.isError,
          slotProps: {
            input: {
              className: styles.input,
              slotProps: {
                root: {
                  className: styleContext({
                    root: true,
                    'time-picker': true
                  })
                }
              },
              startAdornment: <CustomTimePickerAdornment />,
              endAdornment: (
                <ClearInputAdornment
                  onClear={customProps.onClear}
                  inputValue={props.value}
                  inputIsDisabled={props.disabled}
                />
              )
            }
          }
        }
      }}
    />
  );
}

function asTimePicker() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<Dayjs | null>(field.name);

    const { dirty, validateForm } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        if (value && initialValue) {
          // Determine if the dayjs value is different
          return value.diff(initialValue) !== 0;
        } else if (!value && !initialValue) {
          // Input was null, and is still null
          return false;
        } else {
          // It was null, and now it is not or vice versa
          return true;
        }
      }
      return false;
    }, [value, initialValue, dirty]);

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState<Dayjs | null>(value ?? null);

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      setMyValue((state) => {
        if (value !== state) {
          return value ?? null;
        }
        return state;
      });
      void validateForm();
    }, [value, validateForm]);

    /** When the local state has changed, compare to the form value. After a short delay, update the form if the value differs. */
    useDebouncer(
      () => {
        if (myValue !== value) {
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, myValue, true);
          void validateForm();
        }
      },
      [myValue],
      300
    );

    return (
      <TimePicker
        name={field.name}
        className={props.className}
        value={myValue}
        onChange={(value) => {
          setMyValue(value);
        }}
        disabled={props.disabled || form.isSubmitting}
        customProps={{
          labelText: props.label ?? '',
          onClear: () => {
            setMyValue(null);
          },
          onBlur: field.onBlur,
          isDirty: isDirty,
          isError: Boolean(form.dirty && error),
          errorText: error,
          required: props.required
        }}
      />
    );
  };
}

export const FormikTimePicker = asTimePicker();
